/**
 * /admin/hot-pilots — sales activation-score dashboard.
 *
 * Lists every org with its current activation score (0-100). Sorted desc.
 * Heat-colored rows: ≥70 emerald (HOT), 50-69 amber (warming), <50 grey (cold).
 * Each row: org name, score breakdown, owner contact, "DM owner" + "Mark contacted" buttons.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Flame, Users, Send, RefreshCw, Mail, Copy, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HotPilots() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(false);
  const [nudgeResult, setNudgeResult] = useState(null);
  const [copied, setCopied] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/hot-pilots/list`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const triggerNudge = async () => {
    setNudging(true);
    setNudgeResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/hot-pilots/nudge-now`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setNudgeResult(r.ok ? { ok: true, ...d } : { ok: false, error: d.detail || `HTTP ${r.status}` });
    } finally { setNudging(false); }
  };

  const copyEmail = async (email) => {
    try { await navigator.clipboard.writeText(email); setCopied(email); setTimeout(() => setCopied(null), 1500); } catch { /* blocked */ }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px">
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="hot-pilots-page">
      <Helmet><title>Hot Pilots · CARTIEAI</title></Helmet>

      <div className="mb-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-rose-500 bg-gradient-to-br from-rose-500 to-orange-500 items-center justify-center shadow-xl shadow-rose-500/40 shrink-0">
            <Flame className="w-7 h-7 text-white drop-shadow-sm" />
          </div>
          <div className="flex-1">
            <Badge className="bg-rose-500/20 border border-rose-400/40 text-rose-200 text-[10px] font-mono tracking-widest mb-2">SALES INTEL</Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 drop-shadow-[0_2px_18px_rgba(244,63,94,0.25)]">
              Hot <span className="text-emerald-400">Pilots</span>
            </h1>
          </div>
          <Button onClick={refresh} disabled={loading} variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        <p className="text-lg text-slate-100 max-w-3xl">
          Every pilot ranked by <span className="text-emerald-400 font-bold">activation score</span>. ≥{data?.hot_threshold || 70}/100 = <span className="text-rose-300 font-bold">hot</span> — DM them within 24h. CARTIE auto-Slack-pings on hot transitions; trigger a fresh scan below.
        </p>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="hot-pilot-summary">
          <Card className="bg-slate-950 text-slate-100 border border-emerald-500/20"><CardContent className="p-4"><div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Total pilots</div><div className="text-3xl font-bold text-white tabular-nums">{data.total}</div></CardContent></Card>
          <Card className="surface-hero surface-hero-accent-strip relative overflow-hidden"><CardContent className="p-4"><div className="text-[10px] font-bold uppercase tracking-widest mb-2 flex items-center gap-1" style={{color:'rgb(225, 29, 72)'}}><Flame className="w-2.5 h-2.5" /> Hot (≥70)</div><div className="text-3xl font-extrabold tabular-nums" style={{color:'rgb(225, 29, 72)'}}>{data.hot_count}</div><div className="text-[10px] text-slate-500 mt-1">DM within 24h</div></CardContent></Card>
          <Card className="bg-slate-950 text-slate-100 border border-emerald-500/30"><CardContent className="p-4"><div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">P2P-eligible (≥50)</div><div className="text-3xl font-bold text-emerald-300 tabular-nums">{data.eligible_count}</div></CardContent></Card>
          <Card className="bg-slate-950 text-slate-100 border border-emerald-500/20"><CardContent className="p-4"><div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">View mode</div><div className="text-sm font-bold text-white">{data.is_super_admin_view ? 'Super-admin (all)' : 'Own org only'}</div></CardContent></Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Button onClick={triggerNudge} disabled={nudging} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="hot-pilot-nudge-btn">
          <Send className="w-4 h-4 mr-1.5" /> {nudging ? 'Scanning…' : 'Trigger Slack nudge scan'}
        </Button>
        {nudgeResult && (
          nudgeResult.ok ? (
            <span className="text-xs text-emerald-300">✓ Scanned {nudgeResult.candidates_seen} hot pilots · {nudgeResult.nudged} freshly Slack-pinged</span>
          ) : (
            <span className="text-xs text-rose-300">{nudgeResult.error}</span>
          )
        )}
      </div>

      <Card className="bg-slate-950 text-slate-100 border border-emerald-500/30" data-testid="hot-pilot-table-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-400" /> Pilot leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">{[0,1,2,3].map(i => <div key={i} className="h-12 bg-slate-800 rounded animate-pulse" />)}</div>
          ) : !data || data.rows.length === 0 ? (
            <div className="text-center py-12 text-slate-400">No pilots yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase text-cyan-300 border-b border-emerald-500/20">
                  <tr>
                    <th className="text-left py-2 font-bold">Org</th>
                    <th className="text-right py-2 font-bold">Score</th>
                    <th className="text-center py-2 font-bold">Status</th>
                    <th className="text-left py-2 font-bold">Owner</th>
                    <th className="text-right py-2 font-bold">Signals</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-500/10">
                  {data.rows.map((r) => {
                    const score = r.score;
                    const heat = score >= 70 ? 'rose' : score >= 50 ? 'amber' : 'slate';
                    const heatBg = score >= 70 ? 'bg-rose-500/5 border-l-2 border-rose-500/60' : score >= 50 ? 'bg-amber-500/5 border-l-2 border-amber-500/40' : 'bg-slate-900';
                    const heatText = score >= 70 ? 'text-rose-300' : score >= 50 ? 'text-amber-300' : 'text-slate-300';
                    return (
                      <tr key={r.org_id} className={`hover:bg-slate-800 ${heatBg}`} data-testid={`hot-pilot-row-${r.org_id}`}>
                        <td className="py-3 px-2">
                          <div className="text-white font-semibold">{r.org_name}</div>
                          <div className="text-[10px] text-slate-400 font-mono">{r.org_id}</div>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <span className={`text-2xl font-bold tabular-nums ${heatText}`}>{score}</span>
                          <span className="text-xs text-slate-400">/100</span>
                        </td>
                        <td className="py-3 px-2 text-center">
                          {score >= 70 ? (
                            <Badge className="bg-rose-500/20 text-rose-200 border border-rose-400/40 text-[10px] font-mono tracking-widest">
                              <Flame className="w-2.5 h-2.5 inline mr-1" /> HOT
                            </Badge>
                          ) : score >= 50 ? (
                            <Badge className="bg-amber-500/20 text-amber-200 border border-amber-400/40 text-[10px] font-mono tracking-widest">WARMING</Badge>
                          ) : (
                            <Badge className="bg-slate-800 text-slate-300 border border-slate-700/40 text-[10px] font-mono tracking-widest">COLD</Badge>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          {r.owner_email ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-300">
                                {r.owner_name && <span className="text-white font-medium">{r.owner_name} · </span>}
                                <code className="text-emerald-300">{r.owner_email}</code>
                              </span>
                              <button onClick={() => copyEmail(r.owner_email)} className="text-cyan-300 hover:text-cyan-200" title="Copy email">
                                {copied === r.owner_email ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                              </button>
                              <a href={`mailto:${r.owner_email}?subject=Quick%20question%20about%20your%20CARTIE%20pilot`} className="text-emerald-300 hover:text-emerald-200" title="Email owner">
                                <Mail className="w-3 h-3" />
                              </a>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">(no email)</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <div className="text-[10px] text-slate-400 font-mono">
                            {r.signals?.pipeline_connected ? '✓P ' : '· '}
                            {r.signals?.week1_report_sent ? '✓W ' : '· '}
                            {r.signals?.has_30d_data ? '✓D ' : '· '}
                            {(r.signals?.loss_maker_count || 0) >= 3 ? '✓L ' : '· '}
                            {(r.signals?.total_ai_cost_30d_usd || 0) > 100 ? '✓C ' : '· '}
                            {(r.signals?.anomaly_count || 0) >= 1 ? '✓A' : '·'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-[10px] text-slate-400 italic mt-3">
            Signal legend: <code className="text-emerald-300">P</code> pipeline · <code className="text-emerald-300">W</code> week1 sent · <code className="text-emerald-300">D</code> 30d data · <code className="text-emerald-300">L</code> ≥3 loss-makers · <code className="text-emerald-300">C</code> cost &gt;$100/30d · <code className="text-emerald-300">A</code> ≥1 anomaly. Slack-nudge auto-fires for new ≥70 transitions (or +10pt re-jumps). Threshold: {data?.hot_threshold || 70}.
          </div>
        </CardContent>
      </Card>
    </div>
    </div>
  );
}
